import React, { useState } from 'react';
import styled from 'styled-components';

const Bar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
  text-align: center;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1b3c66;
`;

const Stars = styled.div`
  display: flex;
  gap: 6px;
`;

const StarButton = styled.button`
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => (props.$on ? '#f59e0b' : '#cbd5e1')};

  &:focus-visible {
    outline: 2px solid #275386;
    outline-offset: 1px;
  }
`;

const Comment = styled.textarea`
  width: 100%;
  min-height: 56px;
  max-height: 110px;
  box-sizing: border-box;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  background: white;
  font: inherit;
  font-size: 13.5px;
  color: #1e293b;
  resize: none;
  outline: none;

  &:focus {
    border-color: #275386;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 8px 18px;
  border: none;
  border-radius: 18px;
  background: linear-gradient(135deg, #3b74b8 0%, #275386 55%, #1b3c66 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: default; }
`;

const LinkButton = styled.button`
  padding: 8px 6px;
  border: none;
  background: transparent;
  color: #275386;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

const Star = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2.8l2.8 5.7 6.3.9-4.6 4.4 1.1 6.2L12 17l-5.6 3 1.1-6.2-4.6-4.4 6.3-.9z" fill="currentColor" />
  </svg>
);

const LABELS = ['Very poor', 'Poor', 'Okay', 'Good', 'Excellent'];

// Replaces the message box once a live chat has ended. After a chat the agent answered it first
// asks for a 1-5 star rating (stored in the CRM's CSAT survey); it can be skipped.
const ChatEndedBar = ({ onNewChat, busy, sessionId, askRating, agentName }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [state, setState] = useState(askRating ? 'asking' : 'done');  // asking | sending | thanked | done
  const [error, setError] = useState(false);

  const submit = async () => {
    if (!rating) return;
    setState('sending');
    setError(false);
    try {
      const { REACT_APP_BACKEND_BOT_URL, REACT_APP_AUTH_TOKEN } = process.env;
      const response = await fetch(`${REACT_APP_BACKEND_BOT_URL}/chat/csat/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `${REACT_APP_AUTH_TOKEN}` },
        body: JSON.stringify({ rating, comment: comment.trim() || null }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setState('thanked');
    } catch (e) {
      setError(true);
      setState('asking');
    }
  };

  if (askRating && (state === 'asking' || state === 'sending')) {
    const name = agentName || 'our support team';
    return (
      <Bar role="group" aria-label="Rate your chat">
        <Title>How was your chat with {name}?</Title>
        <Stars role="radiogroup" aria-label="Rating">
          {LABELS.map((label, index) => (
            <StarButton
              key={label}
              type="button"
              role="radio"
              aria-checked={rating === index + 1}
              aria-label={`${index + 1} star${index ? 's' : ''}: ${label}`}
              title={label}
              $on={index < rating}
              onClick={() => setRating(index + 1)}
            >
              <Star />
            </StarButton>
          ))}
        </Stars>
        <span>{rating ? LABELS[rating - 1] : 'Tap a star to rate'}</span>
        <Comment
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Anything you'd like to add? (optional)"
          aria-label="Comment (optional)"
          maxLength={2000}
        />
        {error && <span role="alert">Couldn't send your rating. Please try again.</span>}
        <Actions>
          <Button type="button" onClick={submit} disabled={!rating || state === 'sending'}>
            {state === 'sending' ? 'Sending…' : 'Submit'}
          </Button>
          <LinkButton type="button" onClick={() => setState('done')}>Skip</LinkButton>
        </Actions>
      </Bar>
    );
  }

  return (
    <Bar role="status">
      <span>{state === 'thanked' ? 'Thanks for your feedback! This chat has ended.' : 'This chat has ended.'}</span>
      <Button type="button" onClick={onNewChat} disabled={busy}>
        {busy ? 'Starting…' : 'Start a new chat'}
      </Button>
    </Bar>
  );
};

export default ChatEndedBar;
